import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useSubscriptionFeatures, useUserCompanyId } from '@/hooks/useSubscriptionFeatures';
import { SubscriptionTier, TIER_NAMES, hasTierAccess } from '@/lib/subscriptionFeatures';
import { UpgradeRequestDialog } from './UpgradeRequestDialog';

interface UpgradeGateProps {
  requiredTier: 'professional' | 'enterprise' | 'enterprise_plus';
  featureName: string;
  children: ReactNode;
}

export function UpgradeGate({ requiredTier, featureName, children }: UpgradeGateProps) {
  const navigate = useNavigate();
  const { companyId } = useUserCompanyId();
  const { subscription, isLoading } = useSubscriptionFeatures(companyId || undefined);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const currentTier = subscription?.tier || 'free';

  // User has sufficient tier - render children
  if (hasTierAccess(currentTier, requiredTier)) {
    return <>{children}</>;
  }

  // User doesn't have access - show upgrade prompt
  return (
    <>
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Card className="max-w-md w-full border-2 border-dashed">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">{featureName}</h3>
              <p className="text-muted-foreground">
                This feature requires <span className="font-medium text-foreground">{TIER_NAMES[requiredTier]}</span> plan or higher.
              </p>
              <p className="text-sm text-muted-foreground">
                You're currently on <span className="font-medium">{TIER_NAMES[currentTier as SubscriptionTier]}</span>.
              </p>
            </div>

            <Button 
              onClick={() => setShowUpgradeDialog(true)}
              className="gap-2"
            >
              Request Upgrade
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      <UpgradeRequestDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        companyId={companyId || undefined}
        currentTier={currentTier as SubscriptionTier}
        suggestedTier={requiredTier}
      />
    </>
  );
}

// Hook for checking tier access - uses the shared utility
export function useCanAccessTier(requiredTier: 'professional' | 'enterprise' | 'enterprise_plus'): boolean {
  const { companyId } = useUserCompanyId();
  const { subscription } = useSubscriptionFeatures(companyId || undefined);
  
  return hasTierAccess(subscription?.tier, requiredTier);
}

// Get tier badge label
export function getTierBadgeLabel(tier: 'professional' | 'enterprise' | 'enterprise_plus'): string {
  switch (tier) {
    case 'professional': return 'PRO';
    case 'enterprise': return 'ENT';
    case 'enterprise_plus': return 'ENT+';
    default: return '';
  }
}
